import { enqueueSnackbar } from 'notistack';
import { Modal, ModalBody } from '@/components/ui/modal';
import { Copy, PaperPlaneTilt } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import {
  getReferralHome,
  sendReferralInvite,
} from '../../../../services/referrals';
import type { ReferralHomeResponse } from '../../../../services/referrals';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/ui/accordion';
import { capture } from '../../../../utils/analytics';
import { formatPrice } from '../../../../utils/pricing';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  readApiErrorCode,
  readApiErrorMessage,
} from '../../../../services/apiError';

interface ReferralModalProps {
  referFriendModalIsOpen: boolean;
  setReferFriendModalIsOpen: (isOpen: boolean) => void;
}

const ReferralModal: React.FC<ReferralModalProps> = ({
  referFriendModalIsOpen,
  setReferFriendModalIsOpen,
}) => {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [referralLink, setReferralLink] = useState('');

  const [referralHome, setReferralHome] = useState<ReferralHomeResponse | null>(
    null
  );

  const [hasReferralHomeFailed, setHasReferralHomeFailed] = useState(false);
  const [isLoadingReferralLink, setIsLoadingReferralLink] = useState(false);
  const currentUser = useSelector((state: RootState) => state.user.profile);

  useEffect(() => {
    if (!referFriendModalIsOpen) return;

    let isMounted = true;

    const loadReferralLink = async () => {
      setIsLoadingReferralLink(true);
      setReferralLink('');
      setReferralHome(null);
      setHasReferralHomeFailed(false);
      try {
        const response = await getReferralHome();
        if (!isMounted) return;
        setReferralHome(response);
        if (!response.enabled || !response.referralLink) {
          return;
        }
        setReferralLink(response.referralLink);
      } catch (error) {
        console.error('Failed to load referral link:', error);
        if (isMounted) {
          setHasReferralHomeFailed(true);
        }
      } finally {
        if (isMounted) {
          setIsLoadingReferralLink(false);
        }
      }
    };

    void loadReferralLink();

    return () => {
      isMounted = false;
    };
  }, [referFriendModalIsOpen]);

  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.error('Clipboard API copy failed:', error);
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      return document.execCommand('copy');
    } catch (error) {
      console.error('Fallback copy failed:', error);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralLink) {
      enqueueSnackbar('Referral link unavailable right now.');
      return;
    }

    const copied = await copyTextToClipboard(referralLink);
    if (!copied) {
      enqueueSnackbar('Unable to copy referral link.');
      return;
    }

    capture('referral_link_copied', {
      source: 'referral_modal',
      referrerEmail: currentUser?.email ?? null,
    });

    enqueueSnackbar('Referral link copied.');
  };

  const handleSendInvite = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      enqueueSnackbar('Enter a valid email address.');
      return;
    }

    setIsSending(true);
    try {
      const response = await sendReferralInvite(trimmedEmail);
      if (!response.enabled) {
        enqueueSnackbar('Referrals are currently unavailable.');
        return;
      }

      if (response.reasonCode === 'DUPLICATE_INVITE_BLOCKED') {
        enqueueSnackbar('You already invited this email.');
        return;
      }

      if (response.sent) {
        capture('referral_invite_sent', {
          source: 'referral_modal',
          referrerEmail: currentUser?.email ?? null,
          refereeEmail: trimmedEmail,
        });

        enqueueSnackbar('Referral invitation sent.');
      } else {
        enqueueSnackbar('Invite saved, but email sending is disabled here.');
      }

      setEmail('');
    } catch (error) {
      console.error('Failed to send referral invite:', error);

      // The invite email limit (429 EMAIL_QUOTA_EXCEEDED): the server's
      // message names the limit, which says more than the generic copy.
      const quotaMessage =
        readApiErrorCode(error) === 'EMAIL_QUOTA_EXCEEDED'
          ? readApiErrorMessage(error)
          : undefined;

      enqueueSnackbar(
        quotaMessage ?? 'Unable to send referral invite right now.'
      );
    } finally {
      setIsSending(false);
    }
  };

  const isReferralHomeLoading = !referralHome && !hasReferralHomeFailed;
  const isReferralProgramPaused = referralHome?.enabled === false;

  // A response without `referrerRewardEligible` (older backend) still counts
  // as eligible; only a loaded `false` switches to the Student Life copy.
  const isReferrerRewardEligible =
    referralHome !== null && referralHome.referrerRewardEligible !== false;

  const referrerCredit = referralHome?.reward?.referrerCreditCents;

  const formattedReferrerCredit =
    referrerCredit === undefined
      ? null
      : `$${formatPrice(referrerCredit / 100)}`;

  const renderHeadline = () => {
    if (!referralHome || isReferralProgramPaused) {
      return 'Refer a Friend';
    }

    if (!isReferrerRewardEligible) {
      return 'Give a Friend a Month Free';
    }

    return formattedReferrerCredit
      ? `Give a Month, Get ${formattedReferrerCredit}`
      : 'Give a Month, Get $20';
  };

  const renderBody = () => {
    if (isReferralHomeLoading) {
      return <p className="!m-0">Loading your referral details…</p>;
    }

    if (isReferralProgramPaused) {
      return (
        <p className="!m-0">Referrals are paused right now. Check back soon.</p>
      );
    }

    if (isReferrerRewardEligible) {
      return (
        <p className="!m-0">
          Refer a friend who subscribes: they get{' '}
          <strong>one month free</strong> and you get{' '}
          <strong>{formattedReferrerCredit ?? '$20'} in account credit</strong>{' '}
          toward your next bill.<sup>*</sup> Thank you for helping Ora grow.
        </p>
      );
    }

    // Student Life members, and the fallback when the fetch failed: no credit
    // is promised.
    return (
      <p className="!m-0">
        Refer a friend who subscribes and they'll get{' '}
        <strong>one month free</strong>!<sup>*</sup> Thank you for helping Ora
        grow.
      </p>
    );
  };

  return (
    <Modal
      open={referFriendModalIsOpen}
      onClose={() => setReferFriendModalIsOpen(false)}
      name={'refer'}
      blurs={true}
      className="modal--glass"
    >
      <ModalBody className="modal_content">
        <div className="referral flex flex-col gap-4">
          <h2 className="!m-0">{renderHeadline()}</h2>
          {renderBody()}
          <div className="share-link flex flex-col gap-2">
            <Label context="modal" htmlFor="referral-link">
              Your Referral Link
            </Label>
            <div className="share-link_row flex items-stretch gap-2 max-mobile:flex-col">
              <Input
                context="modal"
                id="referral-link"
                type="text"
                value={referralLink}
                readOnly
                placeholder={
                  isLoadingReferralLink
                    ? 'Loading referral link...'
                    : 'Referral link unavailable'
                }
              />
              <Button
                context="modal"
                variant="outline"
                onClick={handleCopyReferralLink}
                disabled={isLoadingReferralLink || !referralLink}
                size="lg"
                className="!px-4"
              >
                <Copy />
                Copy
              </Button>
            </div>
          </div>
          {!isReferralProgramPaused && (
            <>
              <div className="or m-y-0">or</div>
              <div className="invite-form flex flex-col gap-2">
                <Input
                  context="modal"
                  id="referral-invite-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="friend@email.com"
                />
                <Button
                  context="modal"
                  onClick={handleSendInvite}
                  disabled={isSending}
                >
                  <PaperPlaneTilt />
                  <span>{isSending ? 'Sending...' : 'Send Invite'}</span>
                </Button>
                <Accordion context="modal" className="referral-terms mt-2">
                  <AccordionSummary className="[&_>div]:justify-center">
                    * View Terms
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="ora-prose">
                      <ul>
                        <li>Your friend must be a new Ora subscriber.</li>
                        {isReferrerRewardEligible && (
                          <>
                            <li>
                              Your credit is added once your friend makes their
                              first payment and applies automatically to your
                              next subscription bill. Student Life members do
                              not receive credit.
                            </li>
                            <li>
                              If your friend cancels within 24 hours, the credit
                              is revoked.
                            </li>
                          </>
                        )}
                        <li>
                          You cannot refer the same friend more than once.
                        </li>
                        <li>
                          Your friend's month free does not stack with other
                          promotions; the best discount is applied.
                        </li>
                      </ul>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ReferralModal;
